import { createApi } from "@reduxjs/toolkit/query/react";
import { createBaseApiWithAuth } from "@/utilities/reduxToolkit";

/**
 * RTK Query slice for the lister's outbound "PUSH" action — REP-205
 * (Listing/Push pillar, Phase 1).
 *
 * Endpoints are keyed by the lister's seller-rep Project id
 * (`listingProjectId`). On-Repsity recipients get an in-app notification +
 * a PUSH ListingInterest row; off-platform recipients go through the
 * existing transactional invite path + an INVITE ListingInterest row.
 *
 * NOTE: the marketing blast / outbox / social-AI assets in the prototype
 * are Phase 2. Phase 1 only carries audience + the transactional
 * invite/notification push.
 *
 * Keep enum + body/result shapes in sync with
 * `repsity-service/src/dtos/push.dto.ts`.
 */

// ─── Enum mirror ───────────────────────────────────────────────────────
// MIRROR is intentionally absent — the mirror is DERIVED (live join on the
// shared Property), never pushed/stored.
export type InterestSource = "PUSH" | "INVITE" | "MANUAL";

/**
 * A single push recipient. Exactly one of `personId` (on-Repsity, existing
 * Person) or `email` (off-platform, graceful-invite path) should be set;
 * if both are present, the backend lets `personId` win. `name` seeds the
 * placeholder Person created on the off-platform invite path.
 */
export interface PushRecipient {
  personId?: string;
  email?: string;
  name?: string;
}

export interface PushBody {
  /** Optional resolved active sheet so the backend can skip re-resolving. */
  listingSheetId?: string;
  recipients: PushRecipient[];
  /** Optional broker note homed on the created ListingInterest rows. */
  note?: string;
}

/** One row per recipient so the FE can reconcile chips after the push. */
export interface PushResultRow {
  personId?: string | null;
  email?: string | null;
  source: InterestSource;
  channel: "NOTIFICATION" | "INVITE";
  listingInterestId?: string | null;
  inviteId?: string | null;
  skipped?: boolean;
  reason?: string;
}

export const pushApi = createApi({
  ...createBaseApiWithAuth({
    reducerPath: "pushApi",
    // :id = the lister's seller-rep Project id (listingProjectId).
    baseUrl: "/api/listings",
  }),
  reducerPath: "pushApi",
  tagTypes: ["Push", "Inbound"],
  endpoints: (builder) => ({
    /**
     * POST /api/listings/:id/push
     *
     * Push the live listing to a set of recipients. Returns one result row
     * per recipient describing the channel taken (NOTIFICATION vs INVITE)
     * and the row/invite ids created.
     */
    pushListing: builder.mutation<
      { data: PushResultRow[] },
      { listingProjectId: string; body: PushBody }
    >({
      query: ({ listingProjectId, body }) => ({
        url: `/${listingProjectId}/push`,
        method: "POST",
        body,
      }),
      // The newly-pushed recipients surface in the inbound "work it" view,
      // so invalidate it for this listing.
      invalidatesTags: (_result, _error, { listingProjectId }) => [
        { type: "Inbound", id: listingProjectId },
      ],
    }),

    /**
     * POST /api/listings/:id/import
     *
     * Lightweight CRM import — seed the inbound view with MANUAL
     * ListingInterest rows from selected personIds + pasted raw emails.
     */
    importInterest: builder.mutation<
      { data: PushResultRow[] },
      {
        listingProjectId: string;
        body: { personIds?: string[]; emails?: string[]; note?: string };
      }
    >({
      query: ({ listingProjectId, body }) => ({
        url: `/${listingProjectId}/import`,
        method: "POST",
        body,
      }),
      invalidatesTags: (_result, _error, { listingProjectId }) => [
        { type: "Inbound", id: listingProjectId },
      ],
    }),
  }),
});

export const { usePushListingMutation, useImportInterestMutation } = pushApi;
